import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { whiteLabelService } from '../services/whiteLabelService';
import { Lock, AlertCircle, CheckCircle2 } from 'lucide-react';
import { supabase } from '../lib/supabase';

export default function ResetPassword() {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [branding, setBranding] = useState(null);
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  const [isValidRecoveryLink, setIsValidRecoveryLink] = useState(false);
  
  const { updatePassword, user, signOut } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Fetch branding settings on component mount
  useEffect(() => {
    const fetchBranding = async () => {
      try {
        const settings = await whiteLabelService?.getSettings();
        setBranding(settings);
      } catch (err) {
        console.error('Failed to load branding:', err);
      }
    };

    fetchBranding();
  }, []);

  // FIXED: Properly verify recovery token with Supabase
  useEffect(() => {
    const verifyRecoveryToken = async () => {
      const type = searchParams?.get('type');
      const token = searchParams?.get('token');
      
      // Check if this is a password recovery flow
      if (type !== 'recovery' || !token) {
        setError('Invalid password reset link. Please request a new one.');
        setIsCheckingAuth(false);
        return;
      }

      try {
        console.log('Verifying password recovery token...');
        
        // CRITICAL FIX: Explicitly verify the OTP token to establish session
        const { data, error: verifyError } = await supabase?.auth?.verifyOtp({
          token_hash: token,
          type: 'recovery',
        });

        if (verifyError) {
          console.error('Token verification error:', verifyError);
          setError(`Unable to verify password reset link: ${verifyError?.message}. Please request a new password reset email.`);
          setIsCheckingAuth(false);
          return;
        }

        // Check if session was established
        if (!data?.session) {
          console.error('No session established after token verification');
          setError('Failed to establish secure session. Please request a new password reset link.');
          setIsCheckingAuth(false);
          return;
        }

        console.log('Recovery token verified successfully, session established');
        setIsValidRecoveryLink(true);
        setIsCheckingAuth(false);

      } catch (err) {
        console.error('Recovery token verification error:', err);
        setError('An error occurred while verifying your reset link. Please request a new password reset email.');
        setIsCheckingAuth(false);
      }
    };

    verifyRecoveryToken();
  }, [searchParams]);

  const validatePasswords = () => {
    if (!newPassword || !confirmPassword) {
      setError('Please enter and confirm your new password');
      return false;
    }

    if (newPassword?.length < 6) {
      setError('Password must be at least 6 characters long');
      return false;
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setError('');
    setSuccessMessage('');

    if (!validatePasswords()) {
      return;
    }

    // Validate that we have a valid recovery session
    if (!isValidRecoveryLink) {
      setError('Your session has expired. Please request a new password reset link.');
      return;
    }

    setLoading(true);

    try {
      // Update the password using Supabase's updateUser method
      const { error: updateError } = await supabase?.auth?.updateUser({
        password: newPassword
      });

      if (updateError) {
        throw updateError;
      }

      setSuccessMessage('Password updated successfully! Signing out and redirecting to login...');
      
      setNewPassword('');
      setConfirmPassword('');
      
      // Sign out and redirect to login after success
      setTimeout(async () => {
        try {
          await signOut();
          navigate('/auth', { 
            replace: true,
            state: { message: 'Password reset successful. Please sign in with your new password.' }
          });
        } catch (err) {
          console.error('Error signing out:', err);
          navigate('/auth', { replace: true });
        }
      }, 2000);
    } catch (err) {
      console.error('Password reset error:', err);
      
      // Provide specific error messages
      const errorMessage = err?.message || 'Failed to update password';
      if (errorMessage?.toLowerCase()?.includes('token') || errorMessage?.toLowerCase()?.includes('session')) {
        setError('Your password reset session has expired. Please request a new password reset link.');
      } else if (errorMessage?.toLowerCase()?.includes('same')) {
        setError('New password must be different from your current password.');
      } else {
        setError(`${errorMessage}. Please try again or request a new reset link if the problem persists.`);
      }
    } finally {
      setLoading(false);
    }
  };

  // Show loading state while verifying token
  if (isCheckingAuth) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-teal-900 via-blue-900 to-cyan-900 flex items-center justify-center p-4">
        <div className="max-w-md w-full">
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-white rounded-full mb-4">
              <Lock className="w-8 h-8 text-teal-600" />
            </div>
            <h2 className="text-2xl font-bold text-white mb-2">Verifying Reset Link...</h2>
            <div className="flex justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
            </div>
            <p className="text-sm text-teal-100 mt-4">
              Please wait while we verify your password reset link...
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-900 via-blue-900 to-cyan-900 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        {/* White Label Branding Header */}
        {branding ? (
          <div className="text-center mb-8">
            {branding?.logoUrl ? (
              <div className="flex justify-center mb-4">
                <img 
                  src={branding?.logoUrl} 
                  alt={branding?.brandName || 'Company Logo'} 
                  className="h-16 object-contain"
                />
              </div>
            ) : (
              <div className="mb-4">
                <h1 className="text-3xl font-bold text-white">{branding?.brandName || 'FinanceFlow'}</h1>
                {branding?.slogan && (
                  <p className="text-teal-200 mt-2">{branding?.slogan}</p>
                )}
              </div>
            )}
          </div>
        ) : (
          /* Default Header */
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-white rounded-full mb-4">
              <Lock className="w-8 h-8 text-teal-600" />
            </div>
            <h1 className="text-3xl font-bold text-white mb-2">FinanceFlow</h1>
            <p className="text-teal-200">Professional Financial Management</p>
          </div>
        )}

        <div className="text-center mb-8">
          <h2 className="text-2xl font-bold text-white mb-2">Create New Password</h2>
          <p className="text-teal-100">
            Enter your new password to complete the reset process
          </p>
        </div>

        {/* Reset Password Card */}
        <div className="bg-white rounded-lg shadow-xl p-8">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}

          {successMessage && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6 flex items-start gap-3">
              <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-green-800">{successMessage}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* New Password */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                New Password <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="password"
                  required
                  value={newPassword}
                  onChange={(e) => setNewPassword(e?.target?.value)}
                  className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  placeholder="Enter new password"
                  minLength={6}
                  disabled={loading || !isValidRecoveryLink}
                />
              </div>
              <p className="mt-1 text-xs text-gray-500">
                Must be at least 6 characters long
              </p>
            </div>

            {/* Confirm Password */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Confirm Password <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="password"
                  required
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e?.target?.value)}
                  className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  placeholder="Confirm new password"
                  minLength={6}
                  disabled={loading || !isValidRecoveryLink}
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading || !isValidRecoveryLink}
              className="w-full py-3 px-4 bg-teal-600 hover:bg-teal-700 text-white font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  Updating Password...
                </>
              ) : (
                <>
                  <Lock className="w-5 h-5" />
                  Update Password
                </>
              )}
            </button>
          </form>

          <div className="mt-6 text-center">
            <button
              type="button"
              onClick={() => navigate('/auth')}
              className="text-teal-600 hover:text-teal-700 font-medium text-sm"
            >
              Back to Sign In
            </button>
          </div>
        </div>

        <div className="mt-6 text-center">
          <p className="text-sm text-teal-100">
            Having trouble? Contact support at{' '}
            <a href="mailto:support@financeflow.com" className="font-medium underline">
              support@financeflow.com
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}